import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import {
  ExtendedTask,
  PandaDocCreateSessionRequestDto,
  PandaDocSessionResponseDto,
  RelatedSubTask,
  SubTask,
  Task,
  TaskNote,
  TaskNoteType,
  TaskStatus
} from '@k12/models';
import { SHARED_ENVIRONMENT } from '@k12/environment';

export interface TaskFilter {
  taskId?: string;
  assignedAdminAccountId?: string;
  assignedStudentId?: string;
  programPartnerTypeId?: number;
  programPartnerIds?: string[];
  taskStatusIds?: number[];
  taskTypeIds?: string[];
  dueDateStart?: Date;
  dueDateEnd?: Date;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: string;
  searchTerm?: string;
  // replace array-of-dicts with a simple map
  filters?: { [key: string]: string } | null;
}

export interface PagedResultDto<T> {
  items: IEnumerableLike<T>;
  totalCount: number;
  page: number;
  perPage: number;
  totalPages: number;
}

// small helper type so we can accept arrays (what JSON will actually be)
type IEnumerableLike<T> = T[] | ReadonlyArray<T>;

export interface TaskCollectionResponse<T> extends Array<T> {
  items: IEnumerableLike<T>;
  totalCount?: number;
  page?: number;
  perPage?: number;
  totalPages?: number;
}

export interface SavedQuery {
  id: string;
  name: string;
  columns?: string[];
  [key: string]: unknown;
}

// Dictionary<int, int> equivalent
export type TaskStatusCounts = Record<number, number>;

@Injectable({
  providedIn: 'root'
})
export class TasksService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;

  public get(taskId: string): Observable<HttpResponse<Task>> {
    return this.http.get<Task>(`${this.apiUrl}/task/${taskId}`, { observe: 'response' });
  }

  public getRelatedSubTasks(taskId: string): Observable<HttpResponse<RelatedSubTask[]>> {
    return this.http.get<RelatedSubTask[]>(`${this.apiUrl}/task/${taskId}/related-subtasks`, {
      observe: 'response'
    });
  }

  public getTasks(): Observable<HttpResponse<Task[]>> {
    return this.http.get<Task[]>(`${this.apiUrl}/tasks`, { observe: 'response' });
  }

  public getFilteredTasks(filter: TaskFilter): Observable<HttpResponse<TaskCollectionResponse<Task>>> {
    let params = new HttpParams();

    params = this.assignSearchFilters(filter, params);

    const usesPaging = filter.pageNumber !== undefined || filter.pageSize !== undefined;
    const usesSorting = !!filter.orderBy || !!filter.orderDirection;
    const usePagedEndpoint = usesPaging || usesSorting;

    if (usePagedEndpoint) {
      // enforce defaults when using /tasks/paged
      const pageNumber = filter.pageNumber ?? 1;
      const pageSize = filter.pageSize ?? 10;

      params = params.set('pageNumber', pageNumber.toString());
      params = params.set('pageSize', pageSize.toString());

      if (filter.orderBy) params = params.set('orderBy', filter.orderBy);
      if (filter.orderDirection) params = params.set('orderDirection', filter.orderDirection);
    }

    // append arbitrary filters as filters[key]=value
    if (filter.filters) {
      Object.entries(filter.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params = params.append(key, String(value));
        }
      });
    }

    return this.http.get<TaskCollectionResponse<Task>>(
      `${this.apiUrl}${usePagedEndpoint ? '/tasks/paged' : '/tasks'}`,
      {
        params,
        observe: 'response'
      }
    );
  }

  private assignSearchFilters(filter: TaskFilter, params: HttpParams) {
    if (filter.assignedAdminAccountId !== undefined && filter.assignedAdminAccountId !== null) {
      params = params.set('assignedAdminAccountId', String(filter.assignedAdminAccountId));
    }

    if (filter.assignedStudentId) {
      params = params.set('assignedStudentId', filter.assignedStudentId);
    }

    if (filter.taskId) {
      params = params.set('taskId', filter.taskId);
    }

    if (filter.programPartnerTypeId !== undefined && filter.programPartnerTypeId !== null) {
      params = params.set('programPartnerTypeId', filter.programPartnerTypeId.toString());
    }

    if (filter.programPartnerIds && filter.programPartnerIds.length > 0) {
      params = params.set('programPartnerIds', filter.programPartnerIds.join(','));
    }

    if (filter.taskStatusIds && filter.taskStatusIds.length > 0) {
      params = params.append('taskStatusIds', filter.taskStatusIds.join(','));
    }

    if (filter.taskTypeIds && filter.taskTypeIds.length > 0) {
      params = params.append('taskTypeIds', filter.taskTypeIds.join(','));
    }

    if (filter.dueDateStart) {
      params = params.set('dueDateStart', filter.dueDateStart.toISOString());
    }
    if (filter.dueDateEnd) {
      params = params.set('dueDateEnd', filter.dueDateEnd.toISOString());
    }

    if (filter.searchTerm) {
      params = params.set('searchTerm', filter.searchTerm);
    }
    return params;
  }

  public getTaskStatuses(): Observable<HttpResponse<TaskStatus[]>> {
    return this.http.get<TaskStatus[]>(`${this.apiUrl}/tasks/statuses`, { observe: 'response' });
  }

  public getAllowedTaskStatuses(fromStatusId: number, actionType: string): Observable<HttpResponse<TaskStatus[]>> {
    return this.http.get<TaskStatus[]>(`${this.apiUrl}/tasks/statuses/${actionType}/allowed/${fromStatusId}`, {
      observe: 'response'
    });
  }

  public async createTask(task: Task): Promise<HttpResponse<void>> {
    return await lastValueFrom(this.http.post<void>(`${this.apiUrl}/tasks`, task, { observe: 'response' }));
  }

  public async updateTask(task: Task | null): Promise<HttpResponse<unknown>> {
    return await lastValueFrom(this.http.put<unknown>(`${this.apiUrl}/tasks`, task, { observe: 'response' }));
  }

  public async updateTaskNondestructive(task: Task | null): Promise<HttpResponse<unknown>> {
    return await lastValueFrom(
      this.http.put<unknown>(`${this.apiUrl}/tasks/nondestructive`, task, { observe: 'response' })
    );
  }

  // Get all notes for a task
  public getTaskNotes(taskId: string): Observable<HttpResponse<TaskNote[]>> {
    return this.http.get<TaskNote[]>(`${this.apiUrl}/tasks/${taskId}/notes`, { observe: 'response' });
  }

  // Add a new task note
  public async addTaskNote(note: TaskNote): Promise<HttpResponse<TaskNote>> {
    return await lastValueFrom(this.http.post<TaskNote>(`${this.apiUrl}/tasks/notes`, note, { observe: 'response' }));
  }

  public async updateTaskNote(note: TaskNote): Promise<HttpResponse<TaskNote>> {
    return await lastValueFrom(this.http.put<TaskNote>(`${this.apiUrl}/tasks/notes`, note, { observe: 'response' }));
  }

  // Get all task note types
  public getAllTaskNoteTypes(): Observable<HttpResponse<TaskNoteType[]>> {
    return this.http.get<TaskNoteType[]>(`${this.apiUrl}/tasks/notetypes`, { observe: 'response' });
  }

  public getTaskStatusCounts(): Observable<TaskStatusCounts> {
    return this.http.get<TaskStatusCounts>(`${this.apiUrl}/tasks/statuscounts`);
  }

  public isTaskCompleted(task: ExtendedTask | SubTask, taskStatuses: TaskStatus[] | null): boolean {
    // Use dynamic statuses if available, otherwise fall back to string matching
    if (taskStatuses && taskStatuses.length > 0) {
      const finalStatusIds =
        taskStatuses.filter((status) => status.representsFinalStatus)?.map((status) => status.taskStatusId) || [];
      return finalStatusIds.includes(task.taskStatusId);
    }

    // Fallback to string matching for complete statuses
    return ['Accepted', 'Denied'].includes(task.taskStatus);
  }

  // Create PandaDoc session for a task action
  public createPandaDocSession(submission: PandaDocCreateSessionRequestDto): Observable<PandaDocSessionResponseDto> {
    return this.http.post<PandaDocSessionResponseDto>(
      `${this.apiUrl}/pandadoc/documents/${submission.documentId}/session`,
      submission
    );
  }

  public getSavedQueries(): Observable<SavedQuery[]> {
    return this.http.get<SavedQuery[]>(`${this.apiUrl}/tasks/savedQueries`);
  }

  public isAction(task: ExtendedTask, forRecipient: boolean, taskStatuses: TaskStatus[] | null): boolean {
    if (!this.isTaskCompleted(task, taskStatuses)) {
      const taskStatus = taskStatuses?.find((status) => status.taskStatusId === task.taskStatusId);
      const isEditableByRecipient = (forRecipient && taskStatus?.isEditableByProgramPartner) || false;
      const isEditableByCreator = !forRecipient && !(task?.completed || false);
      const hasMultipleSubtasks = task.subTasks && task.subTasks.length > 1;
      return (
        (isEditableByCreator || isEditableByRecipient) &&
        (!hasMultipleSubtasks ||
          task.subTasks?.find((x) => this.isSubAction(x, forRecipient, taskStatuses)) != undefined)
      );
    } else return false;
  }

  private isSubAction(subtask: SubTask, forRecipient: boolean, taskStatuses: TaskStatus[] | null): boolean {
    if (!this.isTaskCompleted(subtask, taskStatuses)) {
      const taskStatus = taskStatuses?.find((status) => status.taskStatusId === subtask.taskStatusId);
      const isEditableByRecipient = (forRecipient && taskStatus?.isEditableByProgramPartner) || false;
      const isEditableByCreator = !forRecipient;
      return isEditableByCreator || isEditableByRecipient;
    } else return false;
  }
}
